import { api, ApiError } from "./api.ts";

/** Recoverable guest-entry outcomes. Unknown causes never reach the screen. */
export type GuestEntryFailure =
  | "missing_link"
  | "invalid_link"
  | "site_paused"
  | "rate_limited"
  | "invalid_name"
  | "unavailable"
  | "cancelled";

type GuestExchangeResult =
  | { readonly ok: true; readonly launchUrl: string }
  | { readonly ok: false; readonly reason: GuestEntryFailure };

/**
 * Browser boundary for an accountless guest link. The reusable credential is
 * private, memory-only, and cannot accidentally serialize into UI diagnostics.
 */
export class GuestEntry {
  #secret: string | null;

  private constructor(secret: string | null) {
    this.#secret = secret;
  }

  /** Parse only the dedicated guest route. The caller must scrub its URL. */
  static fromUrl(url: URL): GuestEntry | null {
    if (url.pathname !== "/guest" && url.pathname !== "/guest/") return null;
    const parameters = new URLSearchParams(url.hash.slice(1));
    const values = parameters.getAll("appnotes_guest");
    const secret = values[0];
    return new GuestEntry(
      values.length === 1 &&
        parameters.size === 1 &&
        secret !== undefined &&
        secret.length > 0 &&
        secret.length <= 512
        ? secret
        : null,
    );
  }

  /** Whether this page still holds a link to exchange. Not an access check. */
  get hasLink(): boolean {
    return this.#secret !== null;
  }

  /** Forget the reusable link when this document is left, including BFCache. */
  clear(): void {
    this.#secret = null;
  }

  /** Exchange through the existing API, with no login or automatic retry. */
  async exchange(
    name: string,
    signal: AbortSignal,
  ): Promise<GuestExchangeResult> {
    const displayName = name.trim();
    if (this.#secret === null) return { ok: false, reason: "missing_link" };
    if (displayName.length === 0 || displayName.length > 100) {
      return { ok: false, reason: "invalid_name" };
    }
    try {
      const response = await api<unknown>("/v1/guest-sessions", {
        method: "POST",
        body: { secret: this.#secret, display_name: displayName },
        signal,
      });
      if (signal.aborted || this.#secret === null)
        return { ok: false, reason: "cancelled" };
      const launchUrl = parseLaunchUrl(response);
      return launchUrl === null
        ? { ok: false, reason: "unavailable" }
        : { ok: true, launchUrl };
    } catch (cause) {
      if (signal.aborted) return { ok: false, reason: "cancelled" };
      if (cause instanceof ApiError) {
        if (cause.status === 404) return { ok: false, reason: "invalid_link" };
        if (cause.code === "site_paused")
          return { ok: false, reason: "site_paused" };
        if (cause.status === 429) return { ok: false, reason: "rate_limited" };
        if (cause.status === 422) return { ok: false, reason: "invalid_name" };
      }
      return { ok: false, reason: "unavailable" };
    }
  }
}

function parseLaunchUrl(response: unknown): string | null {
  if (
    typeof response !== "object" ||
    response === null ||
    !("launch_url" in response) ||
    typeof response.launch_url !== "string" ||
    response.launch_url.length > 16384
  )
    return null;
  try {
    const url = new URL(response.launch_url);
    const parameters = new URLSearchParams(url.hash.slice(1));
    const token = parameters.get("appnotes_token");
    if (
      (url.protocol !== "https:" && url.protocol !== "http:") ||
      url.username !== "" ||
      url.password !== "" ||
      parameters.size !== 1 ||
      token === null ||
      token.length === 0
    )
      return null;
    return url.href;
  } catch {
    return null;
  }
}
